import { useState } from "react";
import { Phone, Mail, MapPin, ChevronDown, Send } from "lucide-react";
import { toast } from "../components/ui/sonner";
import { Reveal } from "./Reveal";
import { CONTACT, SERVICE_OPTIONS, buildWhatsappLink } from "../lib/data";

const WhatsappIcon = ({ size = 18, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-1.746-.873-2.888-1.56-4.036-3.54-.305-.524.305-.487.873-1.62.098-.198.049-.371-.05-.52-.099-.148-.669-1.611-.916-2.16-.242-.53-.487-.457-.67-.467-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.056 3.145 4.983 4.29 2.927 1.144 2.927.763 3.874.714.947-.05 3.057-1.245 3.487-2.446.43-1.2.43-2.223.297-2.437-.13-.198-.297-.297-.594-.446z" />
    <path d="M12.014 2.003C6.492 2.003 2 6.494 2 12.017c0 1.958.55 3.784 1.502 5.34L2 22l4.783-1.462a9.96 9.96 0 0 0 5.23 1.45h.001c5.523 0 10.014-4.492 10.014-10.015 0-5.522-4.49-9.98-10.014-9.97zm5.88 14.11c-.663 1.284-2.502 2.407-3.475 2.5-.973.093-1.973.454-6.664-1.593-4.69-2.047-6.234-6.42-6.234-6.42-.262-.353-1.71-2.13-1.71-4.28 0-2.148 1.13-3.216 1.535-3.65.404-.435.884-.544 1.178-.544h.842c.27 0 .636-.05.994.766.372.85 1.267 3.126 1.38 3.353.114.227.19.494.038.792-.15.297-.226.48-.443.74-.219.26-.46.583-.657.783-.227.227-.464.472-.2.94.264.47 1.174 2.024 2.523 3.28 1.732 1.612 3.213 2.11 3.705 2.35.492.24.78.202 1.067-.135.288-.335 1.234-1.435 1.564-1.928.33-.492.66-.41 1.113-.245.454.166 2.86 1.35 3.35 1.596.49.246.816.369.936.573.12.204.12 1.183-.542 2.467z" />
  </svg>
);

const FIELD_CLASS =
  "w-full rounded-xl bg-white/[0.03] border border-border-subtle px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-laser/60 focus:bg-white/[0.05] transition-colors duration-200";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    description: "",
    budget: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.phone.trim()) errs.phone = "Phone / WhatsApp number is required";
    else if (!/^[+0-9\s()-]{7,}$/.test(form.phone.trim()))
      errs.phone = "Enter a valid phone number";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = "Enter a valid email address";
    if (!form.service) errs.service = "Please select a service";
    if (!form.description.trim()) errs.description = "Please describe your project";
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const message = `New Project Enquiry - Dotnet Services

Name: ${form.name}
Phone: ${form.phone}
Email: ${form.email}
Service Required: ${form.service}
Estimated Budget: ${form.budget || "Not specified"}

Project Description:
${form.description}`;

    window.open(buildWhatsappLink(message), "_blank");
    toast.success(
      "Thank you! Your project enquiry has been received. Our team will contact you shortly."
    );
    setForm({ name: "", phone: "", email: "", service: "", description: "", budget: "" });
    setErrors({});
  };

  return (
    <section id="contact" className="relative py-20 sm:py-28 lg:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl mb-12 sm:mb-16">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-laser">
            // Start a Conversation
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 mb-4">
            Let's Build Something Together
          </h2>
          <p className="text-slate-400 text-base leading-relaxed">
            Have an application, web application, or website idea? Tell us what you want to
            build and let's discuss your project.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <Reveal delay={0.1} className="lg:col-span-5 space-y-4">
            <a
              href={CONTACT.phoneLink}
              data-testid="contact-call-now-link"
              className="flex items-center gap-4 rounded-xl border border-border-subtle bg-card-dark/70 p-5 hover:border-electric/50 transition-colors duration-300 group"
            >
              <div className="h-11 w-11 rounded-full bg-electric/15 border border-electric/30 flex items-center justify-center shrink-0 group-hover:bg-electric/25 transition-colors">
                <Phone size={19} className="text-laser" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Call Now</p>
                <p className="text-white font-medium text-sm sm:text-base">{CONTACT.phoneDisplay}</p>
              </div>
            </a>

            <a
              href={buildWhatsappLink(
                `Hello Dotnet Services,\n\nI am interested in developing a digital project.\n\nService required:\nMobile App / Web Application / Website\n\nPlease contact me to discuss the project.`
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="flex items-center gap-4 rounded-xl border border-border-subtle bg-card-dark/70 p-5 hover:border-whatsapp/60 transition-colors duration-300 group"
            >
              <div className="h-11 w-11 rounded-full bg-whatsapp/15 border border-whatsapp/40 flex items-center justify-center shrink-0 group-hover:bg-whatsapp/25 transition-colors">
                <WhatsappIcon size={19} className="text-whatsapp" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Chat on WhatsApp</p>
                <p className="text-white font-medium text-sm sm:text-base">{CONTACT.phoneDisplay}</p>
              </div>
            </a>

            <a
              href={CONTACT.emailLink}
              data-testid="contact-email-link"
              className="flex items-center gap-4 rounded-xl border border-border-subtle bg-card-dark/70 p-5 hover:border-electric/50 transition-colors duration-300 group"
            >
              <div className="h-11 w-11 rounded-full bg-electric/15 border border-electric/30 flex items-center justify-center shrink-0 group-hover:bg-electric/25 transition-colors">
                <Mail size={19} className="text-laser" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Send Email</p>
                <p className="text-white font-medium text-sm sm:text-base break-all">{CONTACT.email}</p>
              </div>
            </a>

            <div
              className="flex items-start gap-4 rounded-xl border border-border-subtle bg-card-dark/70 p-5"
              data-testid="contact-address-block"
            >
              <div className="h-11 w-11 rounded-full bg-electric/15 border border-electric/30 flex items-center justify-center shrink-0">
                <MapPin size={19} className="text-laser" />
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Address</p>
                {CONTACT.addressLines.map((line) => (
                  <p key={line} className="text-white text-sm leading-relaxed">
                    {line}
                  </p>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="enquiry-form"
              className="rounded-2xl border border-border-subtle bg-card-dark/70 p-6 sm:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">Name *</label>
                  <input
                    data-testid="enquiry-form-name-input"
                    className={FIELD_CLASS}
                    placeholder="Your full name"
                    value={form.name}
                    onChange={handleChange("name")}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-400 mt-1" data-testid="enquiry-form-name-error">
                      {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">Phone / WhatsApp *</label>
                  <input
                    data-testid="enquiry-form-phone-input"
                    className={FIELD_CLASS}
                    placeholder="+91 98765 43210"
                    value={form.phone}
                    onChange={handleChange("phone")}
                  />
                  {errors.phone && (
                    <p className="text-xs text-red-400 mt-1" data-testid="enquiry-form-phone-error">
                      {errors.phone}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Email *</label>
                <input
                  data-testid="enquiry-form-email-input"
                  className={FIELD_CLASS}
                  placeholder="you@company.com"
                  value={form.email}
                  onChange={handleChange("email")}
                />
                {errors.email && (
                  <p className="text-xs text-red-400 mt-1" data-testid="enquiry-form-email-error">
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Service Required *</label>
                <div className="relative">
                  <select
                    data-testid="enquiry-form-service-select"
                    className={`${FIELD_CLASS} appearance-none pr-10 ${!form.service ? "text-slate-500" : ""}`}
                    value={form.service}
                    onChange={handleChange("service")}
                  >
                    <option value="">Select a service</option>
                    {SERVICE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt} className="text-black">
                        {opt}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
                  />
                </div>
                {errors.service && (
                  <p className="text-xs text-red-400 mt-1" data-testid="enquiry-form-service-error">
                    {errors.service}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Project Description *</label>
                <textarea
                  data-testid="enquiry-form-description-input"
                  className={`${FIELD_CLASS} min-h-[110px] resize-none`}
                  placeholder="Tell us about your project idea, goals, and requirements..."
                  value={form.description}
                  onChange={handleChange("description")}
                />
                {errors.description && (
                  <p className="text-xs text-red-400 mt-1" data-testid="enquiry-form-description-error">
                    {errors.description}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs text-slate-400 mb-1.5">
                  Estimated Budget (Optional)
                </label>
                <input
                  data-testid="enquiry-form-budget-input"
                  className={FIELD_CLASS}
                  placeholder={"e.g. \u20b950,000 \u2013 \u20b91,00,000"}
                  value={form.budget}
                  onChange={handleChange("budget")}
                />
              </div>

              <button
                type="submit"
                data-testid="enquiry-form-submit-button"
                className="w-full rounded-full bg-electric hover:bg-electric/90 text-white font-semibold text-sm py-3.5 flex items-center justify-center gap-2 shadow-[0_0_24px_-6px_rgba(0,102,255,0.7)] hover:shadow-[0_0_32px_-4px_rgba(0,102,255,0.9)] transition-all duration-200"
              >
                Request a Quote <Send size={16} />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
